import type { Game, GameContext, GameMeta, InputState } from '../types';
import { playExplosion, playPowerup, playShoot } from '../../audio/sfx';
import { SonicSiloEngine } from './SonicSiloEngine';
import { CanvasRenderer } from './CanvasRenderer';

export class SonicSiloGame implements Game {
  public readonly meta: GameMeta = {
    id: 'sonic-silo',
    title: 'SONIC SILO DEFENSE',
    blurb: '3-Silo Missile Command + Shielded Cities. Trigger EMP Stasis blasts, Gravity Auras & shockwave cascades!',
    tag: 'ARCADE',
    status: 'playable',
  };

  private engine: SonicSiloEngine;
  private renderer!: CanvasRenderer;
  private ctx!: GameContext;
  private canvas!: HTMLCanvasElement;

  private boundMouseMove!: (e: MouseEvent) => void;
  private boundMouseDown!: (e: MouseEvent) => void;
  private boundTouchStart!: (e: TouchEvent) => void;
  private boundTouchMove!: (e: TouchEvent) => void;

  constructor() {
    this.engine = new SonicSiloEngine();
  }

  public mount(ctx: GameContext): void {
    this.ctx = ctx;
    this.canvas = ctx.canvas ?? document.createElement('canvas');
    this.canvas.width = this.engine.width;
    this.canvas.height = this.engine.height;
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.objectFit = 'contain';
    this.canvas.style.cursor = 'crosshair';

    ctx.mount.innerHTML = '';
    ctx.mount.appendChild(this.canvas);

    this.renderer = new CanvasRenderer(this.canvas);
    this.engine.reset();

    this.boundMouseMove = (e: MouseEvent) => this.handlePointerMove(e.clientX, e.clientY);
    this.boundMouseDown = (e: MouseEvent) => {
      if (e.button === 0) {
        this.handlePointerAction(e.clientX, e.clientY);
      }
    };
    this.boundTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        this.handlePointerAction(touch.clientX, touch.clientY);
      }
    };
    this.boundTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        this.handlePointerMove(touch.clientX, touch.clientY);
      }
    };

    this.canvas.addEventListener('mousemove', this.boundMouseMove);
    this.canvas.addEventListener('mousedown', this.boundMouseDown);
    this.canvas.addEventListener('touchstart', this.boundTouchStart, { passive: true });
    this.canvas.addEventListener('touchmove', this.boundTouchMove, { passive: true });
  }

  private getCanvasCoords(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!this.canvas) return null;
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;

    const scaleX = this.engine.width / rect.width;
    const scaleY = this.engine.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }

  private handlePointerMove(clientX: number, clientY: number): void {
    const coords = this.getCanvasCoords(clientX, clientY);
    if (coords) {
      this.engine.setReticlePosition(coords.x, coords.y);
    }
  }

  private handlePointerAction(clientX: number, clientY: number): void {
    const coords = this.getCanvasCoords(clientX, clientY);
    if (coords) {
      this.engine.setReticlePosition(coords.x, coords.y);
    }

    if (this.engine.phase === 'START' || this.engine.phase === 'GAME_OVER') {
      this.engine.startGame();
    } else if (this.engine.phase === 'PLAYING') {
      const bands = this.ctx.getBands();
      const isBeatPeak = Boolean(bands && (bands.beat > 0.55 || bands.kick > 0.55));
      this.engine.fireRocket(this.engine.reticle.x, this.engine.reticle.y, isBeatPeak);
    }
  }

  public update(dt: number, input: InputState): void {
    const bands = this.ctx.getBands();

    const prevShots = this.engine.shotsFiredCount;
    const prevExplosions = this.engine.explosionsTriggeredCount;
    const prevPowerUps = this.engine.powerUpsTriggeredCount;
    const prevImpacts = this.engine.groundImpactsCount;

    this.engine.update(dt, input, bands);

    if (this.engine.shotsFiredCount > prevShots) {
      playShoot();
    }
    if (this.engine.powerUpsTriggeredCount > prevPowerUps) {
      playPowerup();
      this.renderer.triggerShake(0.2, 5);
    }
    if (this.engine.groundImpactsCount > prevImpacts) {
      playExplosion();
      this.renderer.triggerShake(0.35, 9);
      if (navigator.vibrate) navigator.vibrate([40, 60, 40]);
    } else if (this.engine.explosionsTriggeredCount > prevExplosions) {
      playExplosion();
      if (this.engine.combo > 1) {
        this.renderer.triggerShake(0.18, 4 + Math.min(6, this.engine.combo));
      }
    }

    this.renderer.render(this.engine, dt, bands);
  }

  public resize(_w: number, _h: number): void {
    // Internal canvas resolution stays 800x600 with CSS object-fit: contain
  }

  public getHud(): { score: number; lives: number; wave: number; message?: string } {
    const stats = this.engine.getStats();
    return {
      score: stats.score,
      lives: stats.lives,
      wave: stats.wave,
      message: stats.message,
    };
  }

  public dispose(): void {
    if (this.canvas) {
      this.canvas.removeEventListener('mousemove', this.boundMouseMove);
      this.canvas.removeEventListener('mousedown', this.boundMouseDown);
      this.canvas.removeEventListener('touchstart', this.boundTouchStart);
      this.canvas.removeEventListener('touchmove', this.boundTouchMove);
      if (this.canvas.parentElement) {
        this.canvas.parentElement.removeChild(this.canvas);
      }
    }
  }
}
